import Cookies from "js-cookie";
import { getDomain } from "../../CookieHelper";
import env from "../../env.config";

/**
 * Meta's click identifier, set when a visit arrives from an ad.
 * The name must be exactly `_fbc` -- Meta's own `fbevents.js` reads it off the
 * first-party domain and attaches it to every `/tr/` call.
 */
export const FBC_COOKIE = "_fbc";

/** Meta's browser identifier. Minted by the pixel; we only ever read it. */
const FBP_COOKIE = "_fbp";

/**
 * `fbclid` is useful to Meta for ~7 days, but the stored value is the source of
 * truth rather than the visit, so it is kept far longer and overwritten by a
 * later click.
 */
const FBC_TTL_DAYS = 90;

/**
 * The `fb.<subdomainIndex>.…` position, which counts the labels of the domain the
 * cookie is set on: 0 for a bare TLD, 1 for an apex domain, 2 for a subdomain.
 *
 * Hard-coded to 1 because that is correct for every production host: `getDomain`
 * returns the apex, `.prepit.app` for our own domains and the registrable domain
 * for a restaurant's custom one. It is technically wrong on dev, where the cookie
 * lands on `dev.prepit.app` (a subdomain, so 2) -- accepted, because dev traffic
 * is not attributed and Meta is tolerant of the index. If a production host ever
 * sets this cookie on a subdomain, compute it instead of widening this comment.
 */
const FBC_SUBDOMAIN_INDEX = 1;

/**
 * Builds the click identifier in the exact shape Meta expects:
 * `fb.1.<click_timestamp_ms>.<fbclid>`, with the `fbclid` taken verbatim.
 *
 * Getting the shape wrong makes Meta drop the identifier silently -- there is no
 * error and no warning, attribution simply stops working -- so this is kept as a
 * single tested function rather than inlined at the call site.
 */
export const buildFbc = (
  fbclid: string,
  clickTimeMs: number = Date.now()
): string => `fb.${FBC_SUBDOMAIN_INDEX}.${clickTimeMs}.${fbclid}`;

/** The `fbclid` embedded in a stored `_fbc`, or undefined if it is malformed. */
const parseFbclid = (fbc: string | undefined): string | undefined => {
  if (!fbc) {
    return undefined;
  }

  // Split with a limit so an fbclid containing dots survives intact.
  const parts = fbc.split(".");

  return parts.length >= 4 ? parts.slice(3).join(".") : undefined;
};

/**
 * Note these read Meta's cookies directly rather than through `CookieHelper`,
 * which namespaces every key as `${environment}-${key}` and so structurally
 * cannot address a cookie named `_fbc`.
 */
const readCookie = (name: string): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return Cookies.get(name);
  } catch {
    // Cookies can be unavailable entirely (in-app webviews, hardened browsers).
    return undefined;
  }
};

export const readFbc = (): string | undefined => readCookie(FBC_COOKIE);

export const readFbp = (): string | undefined => readCookie(FBP_COOKIE);

/**
 * The page an event happened on, without its query string.
 *
 * Meta requires a real page URL -- `""` is invalid for web attribution -- and
 * wants the full one, which only the browser knows. The query is dropped on
 * purpose: it can carry `fbclid`, a phone number or a table id, and this value
 * is persisted server-side and forwarded to Meta. The backend additionally
 * checks the host against the restaurant's own before trusting it.
 */
export const getEventSourceUrl = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return `${window.location.origin}${window.location.pathname}`;
  } catch {
    return undefined;
  }
};

export const readMetaClickIds = (): { fbc?: string; fbp?: string } => ({
  fbc: readFbc(),
  fbp: readFbp(),
});

/**
 * Stores `_fbc` when the current URL carries an `fbclid`.
 *
 * Last click wins: a visit arriving with a *different* `fbclid` overwrites what
 * is stored, so attribution follows the most recent ad rather than the first one
 * ever seen. Re-landing with the same `fbclid` leaves the original click time
 * alone, since that timestamp is meant to be when the click happened.
 *
 * Never writes `_fbp`. Only Meta's pixel mints that, and a value we invented
 * would corrupt attribution rather than improve it.
 */
export const captureFbclid = (
  fbclid: string | null | undefined,
  now: number = Date.now()
): void => {
  if (typeof window === "undefined" || !fbclid) {
    return;
  }

  if (parseFbclid(readFbc()) === fbclid) {
    return;
  }

  try {
    Cookies.set(FBC_COOKIE, buildFbc(fbclid, now), {
      ...(env.enviroment !== "local" && {
        domain: getDomain(),
        sameSite: "None",
        secure: true,
      }),
      expires: FBC_TTL_DAYS,
    });
  } catch {
    // A visitor who blocks cookies simply goes unattributed; nothing else in the
    // add-to-cart path depends on this succeeding.
  }
};
